/**
 * Thin wrapper over the backend JSON API. Same API on every deployment tier
 * (ADR-0008); the server reports which capabilities are live. Auth is Sanctum
 * SPA session: a cookie set by `POST /api/login`, guarded by the CSRF token
 * from `GET /sanctum/csrf-cookie` on every mutating request.
 */

import type {
  AlertChannel,
  AlertItem,
  AlertList,
  AlertRule as AlertRuleType,
  ApiToken,
  AuditPage,
  BillingOverview,
  BillingPlan,
  Branding,
  BrandingAssetKind,
  BrandingSettings,
  AvailabilityReport,
  ClaimCodeResponse,
  PairingInfo,
  CommandType,
  ConsoleUser,
  ImpersonationCandidate,
  CreatedApiToken,
  LicenseOverview,
  LicenseOrder,
  PortableLicense,
  CurrentUser,
  DemoAdminUser,
  Display,
  DisplayGroup,
  DisplayImportResult,
  FolderGrantEntry,
  FolderNode,
  InvitedUser,
  Layout,
  LayoutDetail,
  LayoutDocument,
  MediaFolder,
  MediaItem,
  MediaPage,
  MediaScope,
  MediaSort,
  MenuBoard,
  MenuBoardSummary,
  MenuCategoryDraft,
  ModuleCatalogEntry,
  ModuleCatalogResponse,
  RegistryListResponse,
  Paginated,
  Permission,
  PermissionCatalogue,
  PopGroupBy,
  PopReport,
  ReportSummary,
  Role,
  ScheduleLayerDraft,
  ScheduleLayersResponse,
  ServerCapabilities,
  BoxBackupMade,
  BoxBackupState,
  BoxBackupSummary,
  ShareableType,
  SosState,
  SsoConnection,
  SsoConnectionOption,
  SamlMetadataImport,
  WebhookDelivery,
  WebhookEndpoint,
  XiboImportReport,
} from './types.js';

const BASE = import.meta.env.VITE_API_BASE ?? '';

const TENANT_HINT_KEY = 'akarya.tenantHint';

/**
 * `?tenant=<slug>` from the page URL — remembered, so the hint survives the
 * redirect to a bare `/login` — as a query string for the signed-out
 * endpoints that must pick a tenant (branding, SSO options). Empty when
 * there is none: the server then falls back to its configured default.
 */
function tenantQuery(): string {
  let hint = new URLSearchParams(window.location.search).get('tenant')?.trim() ?? '';
  try {
    if (hint) localStorage.setItem(TENANT_HINT_KEY, hint);
    else hint = localStorage.getItem(TENANT_HINT_KEY) ?? '';
  } catch {
    // Storage blocked: the URL hint still applies for this page load.
  }
  return hint ? `?tenant=${encodeURIComponent(hint)}` : '';
}

export interface FieldErrors {
  [field: string]: string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly errors: FieldErrors = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** First message for a field, or the first error of any field. */
  fieldMessage(field?: string): string | undefined {
    if (field) return this.errors[field]?.[0];
    for (const list of Object.values(this.errors)) {
      if (list[0]) return list[0];
    }
    return undefined;
  }
}

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1] ?? '') : undefined;
}

let csrfPrimed = false;

async function primeCsrf(force = false): Promise<void> {
  if (!force && (csrfPrimed || readCookie('XSRF-TOKEN'))) {
    csrfPrimed = true;
    return;
  }
  await fetch(`${BASE}/sanctum/csrf-cookie`, { credentials: 'include' });
  csrfPrimed = true;
}

async function parseError(res: Response): Promise<ApiError> {
  let message = `${res.status} ${res.statusText}`;
  let errors: FieldErrors = {};
  try {
    const body = (await res.json()) as { message?: string; errors?: FieldErrors };
    if (body.message) message = body.message;
    if (body.errors) errors = body.errors;
  } catch {
    /* non-JSON body */
  }
  return new ApiError(res.status, message, errors);
}

type Body = Record<string, unknown> | FormData | undefined;

async function request<T>(method: string, path: string, body?: Body, retrying = false): Promise<T> {
  const mutating = method !== 'GET';
  if (mutating) await primeCsrf(retrying);

  const headers: Record<string, string> = { accept: 'application/json' };
  const token = readCookie('XSRF-TOKEN');
  if (mutating && token) headers['X-XSRF-TOKEN'] = token;

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: payload,
  });

  // A stale XSRF-TOKEN cookie (session expired/rotated server-side while the
  // cookie itself was still present) reads as a CSRF mismatch, not an auth
  // failure. Re-prime once and retry rather than leaving the user stuck
  // until they manually clear cookies.
  if (res.status === 419 && mutating && !retrying) {
    return request<T>(method, path, body, true);
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * POSTs a form (or a raw Blob body) like `request`, but over XHR so
 * `onProgress` can follow the upload (fetch can't report request-body
 * progress). Same CSRF retry.
 */
async function upload<T>(
  path: string,
  form: FormData | Blob,
  onProgress: (loaded: number, total: number) => void,
  retrying = false,
): Promise<T> {
  await primeCsrf(retrying);
  const xhr = new XMLHttpRequest();
  xhr.open('POST', `${BASE}${path}`);
  xhr.withCredentials = true;
  xhr.setRequestHeader('accept', 'application/json');
  if (form instanceof Blob) xhr.setRequestHeader('content-type', 'application/octet-stream');
  const token = readCookie('XSRF-TOKEN');
  if (token) xhr.setRequestHeader('X-XSRF-TOKEN', token);
  xhr.upload.addEventListener('progress', (e) => {
    if (e.lengthComputable) onProgress(e.loaded, e.total);
  });

  const res = await new Promise<Response>((resolve, reject) => {
    xhr.addEventListener('load', () =>
      resolve(new Response(xhr.status === 204 ? null : xhr.responseText, { status: xhr.status, statusText: xhr.statusText })),
    );
    xhr.addEventListener('error', () => reject(new TypeError('Network error during upload')));
    xhr.addEventListener('abort', () => reject(new TypeError('Upload aborted')));
    xhr.send(form);
  });

  if (res.status === 419 && !retrying) return upload<T>(path, form, onProgress, true);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const get = <T>(path: string): Promise<T> => request<T>('GET', path);
const post = <T>(path: string, body?: Body): Promise<T> => request<T>('POST', path, body);
const put = <T>(path: string, body?: Body): Promise<T> => request<T>('PUT', path, body);
const patch = <T>(path: string, body?: Body): Promise<T> => request<T>('PATCH', path, body);
const del = <T>(path: string): Promise<T> => request<T>('DELETE', path);

/** Waits between attempts at one chunk; its length is the retry budget. */
const CHUNK_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/** A dropped connection, a 5xx or a 429 may pass on retry; any other 4xx is a refusal. */
function isTransient(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500 || err.status === 429;
  return err instanceof TypeError;
}

/**
 * Sends a media file as raw-body chunks (`/api/media/uploads`): no PHP upload
 * limit applies beyond one chunk, and a failed chunk is retried on its own
 * instead of restarting the whole file. `onProgress` reports bytes of the file.
 */
async function uploadMediaInChunks(
  file: File,
  folderId: string | null | undefined,
  onProgress?: (loaded: number, total: number) => void,
): Promise<MediaItem> {
  const session = await post<{ uploadId: string; chunkSize: number; chunks: number }>('/api/media/uploads', {
    name: file.name,
    size: file.size,
    folderId: folderId ?? null,
  });
  const base = `/api/media/uploads/${session.uploadId}`;

  try {
    for (let i = 0; i < session.chunks; i++) {
      const start = i * session.chunkSize;
      const chunk = file.slice(start, start + session.chunkSize);
      for (let attempt = 0; ; attempt++) {
        try {
          await upload<{ ok: true }>(`${base}/chunks/${i}`, chunk, (loaded) =>
            onProgress?.(start + Math.min(loaded, chunk.size), file.size),
          );
          break;
        } catch (err) {
          if (attempt >= CHUNK_RETRY_DELAYS_MS.length || !isTransient(err)) throw err;
          await new Promise((r) => setTimeout(r, CHUNK_RETRY_DELAYS_MS[attempt]));
        }
      }
    }
    // Not retried: the server discards the session either way, so a resend could only 404.
    return await post<MediaItem>(`${base}/complete`);
  } catch (err) {
    // Free the server-side chunks now rather than waiting for the idle sweep.
    del(base).catch(() => undefined);
    throw err;
  }
}

interface ListResponse<T> {
  data: T[];
}

export const api = {
  async capabilities(): Promise<ServerCapabilities> {
    try {
      return await get<ServerCapabilities>('/api/capabilities');
    } catch {
      return { tier: 'unknown', push: false };
    }
  },

  async branding(): Promise<Branding> {
    try {
      return await get<Branding>(`/api/branding${tenantQuery()}`);
    } catch {
      return {
        productName: 'Akarya Signage',
        accentColor: '#2563eb',
        supportUrl: null,
        logoUrl: null,
        loginBackgroundUrl: null,
        playerSplashUrl: null,
      };
    }
  },
  brandingSettings: (): Promise<BrandingSettings> => get<BrandingSettings>('/api/settings/branding'),
  updateBranding: (body: Partial<BrandingSettings['settings']>): Promise<BrandingSettings> =>
    put<BrandingSettings>('/api/settings/branding', body),
  uploadBrandingAsset: (kind: BrandingAssetKind, file: File): Promise<BrandingSettings> => {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    return post<BrandingSettings>('/api/settings/branding/asset', form);
  },
  deleteBrandingAsset: (kind: BrandingAssetKind): Promise<BrandingSettings> =>
    del<BrandingSettings>(`/api/settings/branding/asset/${kind}`),

  boxBackupState: (): Promise<BoxBackupState> => get<BoxBackupState>('/api/settings/backup'),
  createBoxBackup: (passphrase: string): Promise<BoxBackupMade> =>
    post<BoxBackupMade>('/api/settings/backup', passphrase ? { passphrase } : {}),
  boxBackupDownloadUrl: (id: string): string => `${BASE}/api/settings/backup/download/${id}`,
  /** Stages a restore; the box applies it when it next starts. 422 with `passphraseRequired` asks for the passphrase. */
  stageBoxRestore: (file: File, passphrase: string): Promise<{ staged: BoxBackupSummary }> => {
    const form = new FormData();
    form.append('file', file);
    if (passphrase) form.append('passphrase', passphrase);
    return post<{ staged: BoxBackupSummary }>('/api/settings/backup/restore', form);
  },
  discardBoxRestore: (): Promise<{ staged: null }> => del<{ staged: null }>('/api/settings/backup/restore'),

  login: (email: string, password: string, remember = false): Promise<CurrentUser> =>
    post<CurrentUser>('/api/login', { email, password, remember }),
  /** Self-service sign-up (404 unless the server has it on); starts a session like `login`. */
  register: (body: {
    name: string;
    email: string;
    password: string;
    passwordConfirmation: string;
  }): Promise<CurrentUser> => post<CurrentUser>('/api/register', { ...body, remember: true }),
  /**
   * Trades a Google Identity Services ID token for a console session. When the
   * server has sign-up on, an unknown Google address gets a new account.
   */
  loginWithGoogle: (idToken: string, remember = false): Promise<CurrentUser> =>
    post<CurrentUser>('/api/login/google', { idToken, remember }),
  /** Akarya Box first run: is setup still open, and does this device need the code from the box? */
  boxSetupStatus: (): Promise<{ open: boolean; codeRequired: boolean }> =>
    get<{ open: boolean; codeRequired: boolean }>('/api/box/setup'),
  /** Creates the owner, renames the place and starts a session. */
  boxSetup: (body: {
    placeName: string;
    password: string;
    email?: string;
    code?: string;
  }): Promise<CurrentUser> => post<CurrentUser>('/api/box/setup', body),
  logout: (): Promise<{ ok: true }> => post('/api/logout'),
  currentUser: (): Promise<CurrentUser> => get<CurrentUser>('/api/user'),

  // Operator impersonation (backend config/impersonation.php). Start/stop answer
  // with the user the session is now signed in as.
  impersonationUsers: (q: string): Promise<ImpersonationCandidate[]> =>
    get<ListResponse<ImpersonationCandidate>>(`/api/impersonation/users?q=${encodeURIComponent(q)}`).then(
      (r) => r.data,
    ),
  startImpersonation: (userId: string): Promise<CurrentUser> =>
    post<CurrentUser>('/api/impersonation', { userId }),
  stopImpersonation: (): Promise<CurrentUser> => del<CurrentUser>('/api/impersonation'),

  // Sharing items with demo administrators (admins only; backend ResourceGrantController).
  demoAdmins: (): Promise<DemoAdminUser[]> =>
    get<ListResponse<DemoAdminUser>>('/api/demo-admins').then((r) => r.data),
  shares: (type: ShareableType, id: string): Promise<number[]> =>
    get<{ userIds: number[] }>(`/api/shares/${type}/${id}`).then((r) => r.userIds),
  setShares: (type: ShareableType, id: string, userIds: number[]): Promise<number[]> =>
    put<{ userIds: number[] }>(`/api/shares/${type}/${id}`, { userIds }).then((r) => r.userIds),

  // Emails a set-password link. Always `ok`, whether or not the account exists.
  forgotPassword: (email: string): Promise<{ ok: true }> =>
    post<{ ok: true }>('/api/forgot-password', { email }),

  // Consumes an invite / forgot-password link (App\Support\SetPasswordUrl on the backend).
  setPassword: (body: {
    email: string;
    token: string;
    password: string;
    passwordConfirmation: string;
  }): Promise<{ ok: true }> => post<{ ok: true }>('/api/set-password', body),

  // Enterprise SSO, OIDC (ADR-0017).
  async ssoOptions(): Promise<SsoConnectionOption[]> {
    try {
      return await get<ListResponse<SsoConnectionOption>>(`/api/auth/sso${tenantQuery()}`).then((r) => r.data);
    } catch {
      return [];
    }
  },
  /** A plain browser navigation, never fetched — it has to leave this origin. */
  ssoRedirectUrl: (connectionId: string): string => `${BASE}/api/auth/sso/${connectionId}/redirect`,

  ssoConnections: (): Promise<SsoConnection[]> =>
    get<ListResponse<SsoConnection>>('/api/settings/sso').then((r) => r.data),
  createSsoConnection: (body: Record<string, unknown>): Promise<SsoConnection> =>
    post<SsoConnection>('/api/settings/sso', body),
  updateSsoConnection: (id: string, body: Record<string, unknown>): Promise<SsoConnection> =>
    put<SsoConnection>(`/api/settings/sso/${id}`, body),
  deleteSsoConnection: (id: string): Promise<{ ok: true }> => del(`/api/settings/sso/${id}`),
  testSsoConnection: (id: string): Promise<{ ok: boolean; error?: string }> =>
    post<{ ok: boolean; error?: string }>(`/api/settings/sso/${id}/test`),
  rotateScimToken: (id: string): Promise<{ token: string }> =>
    post<{ token: string }>(`/api/settings/sso/${id}/scim-token`),
  rotateSamlSpKey: (id: string): Promise<SsoConnection> =>
    post<SsoConnection>(`/api/settings/sso/${id}/saml-sp-key`),
  importSamlMetadata: (source: { xml: string } | { url: string }): Promise<SamlMetadataImport> =>
    post<{ data: SamlMetadataImport }>('/api/settings/sso/saml-metadata/import', source).then((r) => r.data),

  displays: (): Promise<Display[]> => get<ListResponse<Display>>('/api/displays').then((r) => r.data),
  display: (id: string): Promise<Display> => get<Display>(`/api/displays/${id}`),
  createDisplay: (name: string): Promise<Display> => post<Display>('/api/displays', { name }),
  updateDisplay: (id: string, patchBody: Partial<Record<string, unknown>>): Promise<Display> =>
    patch<Display>(`/api/displays/${id}`, patchBody),
  deleteDisplay: (id: string): Promise<{ ok: true }> => del(`/api/displays/${id}`),
  claimCode: (id: string): Promise<ClaimCodeResponse> =>
    post<ClaimCodeResponse>(`/api/displays/${id}/claim-code`),
  /** Device-initiated pairing (spec §2.3): what a player showing this code is. 410 once expired/claimed. */
  pairingInfo: (code: string): Promise<PairingInfo> =>
    get<PairingInfo>(`/api/displays/pairing/${encodeURIComponent(code)}`),
  /** Claim a player's pairing code into this tenant as a new display. */
  claimPairing: (code: string, name: string): Promise<Display> =>
    post<Display>('/api/displays/claim', { code, name }),
  /** The CMS address a player enrolls against — what the claim form's "CMS address" field expects. */
  serverOrigin: (): string => BASE || window.location.origin,
  sendCommand: (id: string, type: CommandType, args?: Record<string, unknown>): Promise<unknown> =>
    post(`/api/displays/${id}/commands`, args ? { type, args } : { type }),
  screenshotUrl: (id: string): string => `${BASE}/api/displays/${id}/screenshot`,
  /** A backend-relative path the API returned (e.g. `MediaItem.url`), made loadable from a separate-origin console. */
  assetUrl: (path: string): string => `${BASE}${path}`,

  displayGroups: (): Promise<DisplayGroup[]> =>
    get<ListResponse<DisplayGroup>>('/api/display-groups').then((r) => r.data),
  createDisplayGroup: (body: { name: string; description?: string | null; displayIds?: string[] }): Promise<DisplayGroup> =>
    post<DisplayGroup>('/api/display-groups', body),
  updateDisplayGroup: (
    id: string,
    body: { name?: string; description?: string | null; displayIds?: string[] },
  ): Promise<DisplayGroup> => patch<DisplayGroup>(`/api/display-groups/${id}`, body),
  deleteDisplayGroup: (id: string): Promise<{ ok: true }> => del(`/api/display-groups/${id}`),
  /** Sets every member's default layout (null clears it). */
  applyDisplayGroupLayout: (id: string, layoutId: string | null): Promise<DisplayGroup & { updated: number }> =>
    post<DisplayGroup & { updated: number }>(`/api/display-groups/${id}/layout`, { layoutId }),

  media: (folderId?: string | null): Promise<MediaItem[]> => {
    const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
    return get<ListResponse<MediaItem>>(`/api/media${qs}`).then((r) => r.data);
  },
  unfiledMedia: (): Promise<MediaItem[]> =>
    get<ListResponse<MediaItem>>('/api/media?unfiled=1').then((r) => r.data),
  /** One page of the library, filtered (`q`) and sorted on the server. */
  mediaPage: (
    scope: MediaScope,
    opts: { offset: number; limit: number; sort: MediaSort; q: string },
  ): Promise<MediaPage> => {
    const qs = new URLSearchParams({ offset: String(opts.offset), limit: String(opts.limit), sort: opts.sort });
    if (scope.kind === 'unfiled') qs.set('unfiled', '1');
    if (scope.kind === 'folder') qs.set('folderId', scope.id);
    if (opts.q) qs.set('q', opts.q);
    return get<MediaPage>(`/api/media?${qs.toString()}`);
  },
  mediaFolders: (): Promise<MediaFolder[]> =>
    get<ListResponse<MediaFolder>>('/api/media/folders').then((r) => r.data),
  uploadMedia: (
    file: File,
    folderId?: string | null,
    onProgress?: (loaded: number, total: number) => void,
  ): Promise<MediaItem> => uploadMediaInChunks(file, folderId, onProgress),
  moveMedia: (id: string, folderId: string | null): Promise<MediaItem> =>
    patch<MediaItem>(`/api/media/${id}`, { folderId }),
  /** 409 while a layout (or module bundle) still references the file. */
  deleteMedia: (id: string): Promise<{ ok: true }> => del(`/api/media/${id}`),

  layouts: (folderId?: string | null): Promise<Layout[]> => {
    const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : '';
    return get<ListResponse<Layout>>(`/api/layouts${qs}`).then((r) => r.data);
  },
  layout: (id: string): Promise<LayoutDetail> => get<LayoutDetail>(`/api/layouts/${id}`),
  createLayout: (name: string, document: LayoutDocument, folderId?: string | null): Promise<Layout> =>
    post<Layout>('/api/layouts', { name, document, folderId }),
  updateLayout: (
    id: string,
    name: string,
    document: LayoutDocument,
    folderId?: string | null,
  ): Promise<Layout> => put<Layout>(`/api/layouts/${id}`, { name, document, folderId }),
  moveLayout: (id: string, folderId: string | null): Promise<Layout> =>
    patch<Layout>(`/api/layouts/${id}/folder`, { folderId }),
  publishLayout: (id: string): Promise<Layout> => post<Layout>(`/api/layouts/${id}/publish`),
  deleteLayout: (id: string): Promise<{ ok: true }> => del(`/api/layouts/${id}`),

  // Emergency SOS override (ADR-0029).
  sos: (): Promise<SosState> => get<SosState>('/api/sos'),
  updateSos: (body: { landscapeLayoutId?: string | null; portraitLayoutId?: string | null }): Promise<SosState> =>
    put<SosState>('/api/sos', body),
  createSosLayouts: (): Promise<SosState> => post<SosState>('/api/sos/layouts'),
  activateSos: (): Promise<SosState> => post<SosState>('/api/sos/activate'),
  clearSos: (): Promise<SosState> => post<SosState>('/api/sos/clear'),

  // Media/layout folders + per-user folder grants (ADR-0024).
  folders: (): Promise<FolderNode[]> => get<ListResponse<FolderNode>>('/api/folders').then((r) => r.data),
  createFolder: (name: string, parentId?: string | null): Promise<FolderNode> =>
    post<FolderNode>('/api/folders', { name, parentId }),
  renameFolder: (id: string, name: string): Promise<FolderNode> =>
    put<FolderNode>(`/api/folders/${id}`, { name }),
  moveFolder: (id: string, parentId: string | null): Promise<FolderNode> =>
    put<FolderNode>(`/api/folders/${id}`, { parentId }),
  deleteFolder: (id: string): Promise<{ ok: true }> => del(`/api/folders/${id}`),
  folderGrants: (folderId: string): Promise<FolderGrantEntry[]> =>
    get<ListResponse<FolderGrantEntry>>(`/api/folders/${folderId}/grants`).then((r) => r.data),
  createFolderGrant: (
    folderId: string,
    body: { userId: number; permissions: Permission[] },
  ): Promise<FolderGrantEntry> => post<FolderGrantEntry>(`/api/folders/${folderId}/grants`, body),
  deleteFolderGrant: (folderId: string, grantId: string): Promise<{ ok: true }> =>
    del(`/api/folders/${folderId}/grants/${grantId}`),

  // Menu boards (ADR-0021).
  menuBoards: (): Promise<MenuBoardSummary[]> =>
    get<ListResponse<MenuBoardSummary>>('/api/menu-boards').then((r) => r.data),
  menuBoard: (id: string): Promise<MenuBoard> => get<MenuBoard>(`/api/menu-boards/${id}`),
  createMenuBoard: (name: string): Promise<MenuBoard> => post<MenuBoard>('/api/menu-boards', { name }),
  updateMenuBoard: (
    id: string,
    body: { name: string; currency: string; categories: MenuCategoryDraft[] },
  ): Promise<MenuBoard> => put<MenuBoard>(`/api/menu-boards/${id}`, body),
  deleteMenuBoard: (id: string): Promise<{ ok: true }> => del(`/api/menu-boards/${id}`),

  // Administration (ADR-0010).
  roles: (): Promise<Role[]> => get<ListResponse<Role>>('/api/roles').then((r) => r.data),
  permissionCatalogue: (): Promise<PermissionCatalogue> => get<PermissionCatalogue>('/api/permissions'),
  createRole: (body: { name: string; label: string; permissions: Permission[] }): Promise<Role> =>
    post<Role>('/api/roles', body),
  updateRole: (
    id: string,
    body: { name: string; label: string; permissions: Permission[] },
  ): Promise<Role> => put<Role>(`/api/roles/${id}`, body),
  deleteRole: (id: string): Promise<{ ok: true }> => del(`/api/roles/${id}`),

  users: (): Promise<ConsoleUser[]> => get<ListResponse<ConsoleUser>>('/api/users').then((r) => r.data),
  inviteUser: (body: { name: string; email: string; role: string; roleId?: string | null }): Promise<InvitedUser> =>
    post<InvitedUser>('/api/users', body),
  updateUser: (
    id: string,
    body: Partial<{ name: string; role: string; roleId: string | null }>,
  ): Promise<ConsoleUser> => patch<ConsoleUser>(`/api/users/${id}`, body),
  deleteUser: (id: string): Promise<{ ok: true }> => del(`/api/users/${id}`),

  auditLogs: (params: Record<string, string> = {}): Promise<AuditPage> => {
    const qs = new URLSearchParams(params).toString();
    return get<AuditPage>(`/api/audit-logs${qs ? `?${qs}` : ''}`);
  },

  // Reporting (ADR-0011).
  reportSummary: (): Promise<ReportSummary> => get<ReportSummary>('/api/reports/summary'),
  proofOfPlay: (params: { from?: string; to?: string; groupBy?: PopGroupBy } = {}): Promise<PopReport> => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return get<PopReport>(`/api/reports/proof-of-play${qs ? `?${qs}` : ''}`);
  },
  proofOfPlayCsvUrl: (params: { from?: string; to?: string; groupBy?: PopGroupBy } = {}): string => {
    const qs = new URLSearchParams({ ...params, format: 'csv' } as Record<string, string>).toString();
    return `${BASE}/api/reports/proof-of-play?${qs}`;
  },
  availability: (params: { from?: string; to?: string } = {}): Promise<AvailabilityReport> => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return get<AvailabilityReport>(`/api/reports/availability${qs ? `?${qs}` : ''}`);
  },

  // Alerting (ADR-0011).
  alerts: (params: { state?: string; severity?: string } = {}): Promise<AlertList> => {
    const qs = new URLSearchParams(params as Record<string, string>).toString();
    return get<AlertList>(`/api/alerts${qs ? `?${qs}` : ''}`);
  },
  acknowledgeAlert: (id: string): Promise<AlertItem> => post<AlertItem>(`/api/alerts/${id}/acknowledge`),
  resolveAlert: (id: string): Promise<AlertItem> => post<AlertItem>(`/api/alerts/${id}/resolve`),

  alertRules: (): Promise<{ data: AlertRuleType[]; kinds: string[] }> =>
    get<{ data: AlertRuleType[]; kinds: string[] }>('/api/alert-rules'),
  saveAlertRule: (id: string | null, body: Record<string, unknown>): Promise<AlertRuleType> =>
    id ? put<AlertRuleType>(`/api/alert-rules/${id}`, body) : post<AlertRuleType>('/api/alert-rules', body),
  deleteAlertRule: (id: string): Promise<{ ok: true }> => del(`/api/alert-rules/${id}`),

  alertChannels: (): Promise<AlertChannel[]> =>
    get<ListResponse<AlertChannel>>('/api/alert-channels').then((r) => r.data),
  saveAlertChannel: (id: string | null, body: Record<string, unknown>): Promise<AlertChannel> =>
    id ? put<AlertChannel>(`/api/alert-channels/${id}`, body) : post<AlertChannel>('/api/alert-channels', body),
  deleteAlertChannel: (id: string): Promise<{ ok: true }> => del(`/api/alert-channels/${id}`),
  testAlertChannel: (id: string): Promise<{ ok: boolean; error?: string }> =>
    post<{ ok: boolean; error?: string }>(`/api/alert-channels/${id}/test`),

  // Platform: API tokens + webhooks (ADR-0012).
  apiTokens: (): Promise<ApiToken[]> =>
    get<ListResponse<ApiToken>>('/api/api-tokens').then((r) => r.data),
  createApiToken: (name: string, abilities: Permission[]): Promise<CreatedApiToken> =>
    post<CreatedApiToken>('/api/api-tokens', { name, abilities }),
  deleteApiToken: (id: string): Promise<{ ok: true }> => del(`/api/api-tokens/${id}`),

  // Self-service portable player licences (Midtrans).
  licenseOverview: (): Promise<LicenseOverview> => get<LicenseOverview>('/api/licenses'),
  buyLicenses: (licenseIds: string[]): Promise<LicenseOrder> =>
    post<{ data: LicenseOrder }>('/api/licenses/checkout', { licenseIds }).then((r) => r.data),
  licenseOrder: (id: string): Promise<LicenseOrder> =>
    get<{ data: LicenseOrder }>(`/api/licenses/orders/${encodeURIComponent(id)}`).then((r) => r.data),
  cancelLicenseOrder: (id: string): Promise<LicenseOrder> =>
    post<{ data: LicenseOrder }>(`/api/licenses/orders/${encodeURIComponent(id)}/cancel`).then((r) => r.data),

  // Operator-only: mint portable player licence keys.
  portableLicenses: (): Promise<PortableLicense[]> =>
    get<{ data: PortableLicense[] }>('/api/settings/portable-licenses').then((r) => r.data),
  issuePortableLicense: (body: {
    licensee: string;
    licenseIds: string[];
    expiresInDays: number | null;
  }): Promise<PortableLicense[]> =>
    post<{ data: PortableLicense | PortableLicense[] }>('/api/settings/portable-licenses', body).then((r) =>
      Array.isArray(r.data) ? r.data : [r.data],
    ),

  webhooks: (): Promise<WebhookEndpoint[]> =>
    get<Paginated<WebhookEndpoint>>('/api/webhooks').then((r) => r.data),
  createWebhook: (body: {
    url: string;
    description?: string | null;
    events: string[];
  }): Promise<WebhookEndpoint> => post<{ data: WebhookEndpoint }>('/api/webhooks', body).then((r) => r.data),
  updateWebhook: (
    id: string,
    body: Partial<{ url: string; description: string | null; events: string[]; enabled: boolean }>,
  ): Promise<WebhookEndpoint> => patch<{ data: WebhookEndpoint }>(`/api/webhooks/${id}`, body).then((r) => r.data),
  deleteWebhook: (id: string): Promise<{ ok: true }> => del(`/api/webhooks/${id}`),
  pingWebhook: (id: string): Promise<{ ok: boolean; data: WebhookDelivery }> =>
    post<{ ok: boolean; data: WebhookDelivery }>(`/api/webhooks/${id}/ping`),
  webhookDeliveries: (id: string): Promise<WebhookDelivery[]> =>
    get<Paginated<WebhookDelivery>>(`/api/webhooks/${id}/deliveries`).then((r) => r.data),

  // Migration imports (ADR-0013).
  importXiboLayout: (file: File, dryRun: boolean): Promise<XiboImportReport> => {
    const form = new FormData();
    form.append('file', file);
    form.append('dryRun', dryRun ? '1' : '0');
    return post<XiboImportReport>('/api/import/xibo/layout', form);
  },
  importDisplays: (names: string[], dryRun: boolean): Promise<DisplayImportResult> =>
    post<DisplayImportResult>('/api/import/displays', { displays: names, dryRun }),

  // Widget module marketplace (ADR-0015).
  modules: (): Promise<ModuleCatalogResponse> => get<ModuleCatalogResponse>('/api/modules'),
  module: (slug: string): Promise<ModuleCatalogEntry> => get<ModuleCatalogEntry>(`/api/modules/${slug}`),
  installModule: (slug: string): Promise<ModuleCatalogEntry> =>
    post<ModuleCatalogEntry>(`/api/modules/${slug}/install`),
  updateModule: (
    slug: string,
    body: Partial<{ enabled: boolean; settings: Record<string, unknown> | null }>,
  ): Promise<ModuleCatalogEntry> => patch<ModuleCatalogEntry>(`/api/modules/${slug}`, body),
  uninstallModule: (slug: string): Promise<{ ok: true }> => del(`/api/modules/${slug}`),
  refreshModuleData: (
    slug: string,
  ): Promise<{ refreshed: { fetched: number; skipped: number; errors: number }; feeds: ModuleCatalogEntry['feeds'] }> =>
    post(`/api/modules/${slug}/refresh-data`),
  uploadModule: (manifest: string, template: File): Promise<ModuleCatalogEntry> => {
    const form = new FormData();
    form.append('manifest', manifest);
    form.append('template', template);
    return post<ModuleCatalogEntry>('/api/modules', form);
  },
  uploadModulePackage: (pkg: File): Promise<ModuleCatalogEntry> => {
    const form = new FormData();
    form.append('package', pkg);
    return post<ModuleCatalogEntry>('/api/modules', form);
  },
  registryModules: (): Promise<RegistryListResponse> => get<RegistryListResponse>('/api/modules/registry'),
  syncRegistry: (): Promise<{ modules: number; warnings: string[] }> =>
    post('/api/modules/registry/sync'),
  installRegistryModule: (
    slug: string,
    opts: { channel?: string; version?: string } = {},
  ): Promise<ModuleCatalogEntry> => post<ModuleCatalogEntry>(`/api/modules/registry/${slug}/install`, opts),

  // SaaS billing (ADR-0019).
  billingPlans: (): Promise<BillingPlan[]> => get<ListResponse<BillingPlan>>('/api/settings/billing/plans').then((r) => r.data),
  billingOverview: (): Promise<BillingOverview> => get<BillingOverview>('/api/settings/billing'),
  // A mutating, authenticated POST (creates a Stripe Customer/Session tied to
  // this tenant, needs CSRF) — unlike `ssoRedirectUrl`, this can't be a bare
  // link; the screen awaits it then navigates to the returned url itself.
  createBillingCheckoutSession: (planSlug: string, interval: 'month' | 'year'): Promise<{ url: string }> =>
    post<{ url: string }>('/api/settings/billing/checkout', { planSlug, interval }),
  createBillingPortalSession: (): Promise<{ url: string }> => post<{ url: string }>('/api/settings/billing/portal'),

  scheduleLayers: (displayId: string): Promise<ScheduleLayersResponse> =>
    get<ScheduleLayersResponse>(`/api/displays/${displayId}/schedule-layers`),
  replaceScheduleLayers: (
    displayId: string,
    layers: ScheduleLayerDraft[],
  ): Promise<ScheduleLayersResponse> =>
    put<ScheduleLayersResponse>(`/api/displays/${displayId}/schedule-layers`, { layers }),
};
